<template>
    <Dialog v-model:open="opened">
        <DialogContent v-if="confirmDialogData" class="p-0 sm:max-w-[900px]">
            <div class="flex w-full">
                <div class="relative hidden w-full bg-white md:block">
                    <img
                        src="https://zaptime.s3.eu-central-1.amazonaws.com/application-assets/bg.png"
                        class="h-[630px] object-cover object-right"
                        alt=""
                    />
                </div>
                <div class="w-full p-6">
                    <p
                        class="mt-4 text-4xl font-bold tracking-tighter text-[#090F23]"
                    >
                        {{ $t("pro_dialog.title") }}
                    </p>
                    <div class="mt-4 flex items-center gap-2">
                        <p
                            class="text-sm font-medium capitalize tracking-tight text-iron-700"
                        >
                            {{ $t("monthly") }}
                        </p>
                        <Switch v-model:checked="yearlyPeriod"> </Switch>

                        <div class="relative">
                            <p
                                class="text-sm font-medium capitalize tracking-tight text-iron-700"
                            >
                                {{ $t("yearly") }}
                            </p>

                            <div
                                v-if="
                                    selectedPlan.currency === 'CZK' &&
                                    locale === 'cs'
                                "
                                class="mark absolute -right-28 -top-2 text-nowrap rounded-xl px-2 text-sm font-bold text-white"
                            >
                                Sleva 1200 Kč
                            </div>

                            <div
                                v-if="
                                    selectedPlan.currency !== 'CZK' &&
                                    locale !== 'cs'
                                "
                                class="mark absolute -right-14 -top-2 text-nowrap rounded-xl px-2 text-sm font-bold text-white"
                            >
                                -20%
                            </div>
                        </div>
                    </div>

                    <div class="mt-2">
                        <p class="text-3xl font-bold tracking-tight">
                            {{
                                formatPriceCurrency(
                                    selectedPlan.recurring === "yearly"
                                        ? selectedPlan.price / 12
                                        : selectedPlan.price,
                                    selectedPlan.currency,
                                )
                            }}/{{ $t("month") }}
                        </p>
                    </div>

                    <ScrollArea class="mt-4 h-[330px]">
                        <ul class="relative mb-10">
                            <li
                                v-for="(value, i) in features"
                                :key="i"
                                class="my-3 flex items-center gap-2"
                                :class="[
                                    i === 0
                                        ? ' border-iron-900 text-xl font-bold text-iron-900'
                                        : 'text-lg font-medium text-iron-500',
                                ]"
                            >
                                <div
                                    class="mark itemce flex h-6 w-6 items-center justify-center rounded-full"
                                >
                                    <Check
                                        class="text-white"
                                        stroke-width="3.5"
                                        :size="16"
                                    />
                                </div>

                                <p class="tracking-tight">
                                    {{ value }}
                                </p>
                            </li>
                        </ul>
                        <div
                            class="z-2 pointer-events-none absolute bottom-0 left-0 h-12 w-full bg-gradient-to-b from-transparent to-white"
                        ></div>
                    </ScrollArea>

                    <div class="mt-6">
                        <Button
                            :size="'lg'"
                            class="h-[64px] w-full text-xl font-bold"
                            @click="confirm"
                            >{{ $t("pro_dialog.title") }}
                        </Button>
                    </div>
                </div>
            </div>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import useConfirm from "../composables/useProDialog";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-vue-next";

import Switch from "@/components/ui/switch/Switch.vue";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useI18n } from "vue-i18n";
import { ScrollArea } from "@/components/ui/scroll-area";
import { router } from "@inertiajs/vue3";
import { usePage } from "@inertiajs/vue3";
import { Plan } from "@/Pages/ZapTime/App/Subscriptions/types";
import { formatPriceCurrency } from "@/Pages/ZapTime/App/Subscriptions/utils/formatPriceCurrency";

const { opened, confirmDialogData } = useConfirm();
const { t, locale } = useI18n();
const page = usePage();

const plans = page.props.plans as Plan[];

const selectedPlan = ref(plans[0]);

const yearlyPeriod = ref(true);

function confirm() {
    opened.value = false;
    router.visit("/billing");
}

watch(yearlyPeriod, () => {
    selectedPlan.value = yearlyPeriod.value ? plans[0] : plans[1];
});

function getFreshMap() {
    const featuresMap = new Map();

    featuresMap.set(
        "unlimited_events",
        t("pro_dialog.features.unlimited_events"),
    );
    featuresMap.set(
        "calendar_managed_availability",
        t("pro_dialog.features.calendar_managed_availability"),
    );
    featuresMap.set(
        "remove_branding",
        t("pro_dialog.features.remove_branding"),
    );
    featuresMap.set(
        "custom_workflow",
        t("pro_dialog.features.custom_workflow"),
    );
    featuresMap.set("analytics", t("pro_dialog.features.analytics"));
    featuresMap.set("custom_domain", t("pro_dialog.features.custom_domain"));
    featuresMap.set("paid_events", t("pro_dialog.features.paid_events"));
    featuresMap.set("integrations", t("pro_dialog.features.integrations"));
    featuresMap.set("booking_limit", t("pro_dialog.features.booking_limit"));
    featuresMap.set(
        "customize_confirmation_email",
        t("pro_dialog.features.customize_confirmation_email"),
    );
    featuresMap.set("days_off", t("pro_dialog.features.days_off"));

    return featuresMap;
}

const features = computed(() => {
    const featuresMap = getFreshMap();

    if (confirmDialogData.value?.feature) {
        const selectedFeature = featuresMap.get(
            confirmDialogData.value?.feature,
        );

        if (selectedFeature) {
            featuresMap.delete(confirmDialogData.value?.feature);

            return [selectedFeature, ...featuresMap.values()];
        }
    }

    return featuresMap.values();
});
</script>

<style>
.mark {
    background: linear-gradient(
        143.16deg,
        #ff8d47 5.29%,
        #ff7447 18.74%,
        #ff4247 46.66%,
        #e92a5b 63.32%,
        #dd1c67 72.74%,
        #d31071 82.09%,
        #cf0283 96.39%
    );
}
</style>
